// ============================================================================
// mediaSettings.ts — ЕДИНЫЕ пользовательские настройки медиаплеера (SSoT):
//   media.speed        — запомненная скорость воспроизведения (0.5–3×);
//   media.autoplay_next — очередь «слушать подряд» (следующее аудио ленты).
// Хранение — settingsStore (user_settings, как все настройки платформы);
// плеер и очередь ТОЛЬКО читают/пишут через этот модуль (SRP, без дублей).
// ============================================================================

import { useEffect, useState } from 'react';
import { settingsStore } from '@/lib/settingsStore';

export const MEDIA_SPEED_KEY = 'media.speed';
export const MEDIA_AUTOPLAY_NEXT_KEY = 'media.autoplay_next';

/** Скорости проигрывателя (0.5–3×); порядок — цикл кнопки «Скорость». */
export const MEDIA_RATES: number[] = [1, 1.25, 1.5, 2, 2.5, 3, 0.5];
export const MEDIA_SPEED_MIN = 0.5;
export const MEDIA_SPEED_MAX = 3;

/** Приведение скорости в допустимый диапазон (0.5–3×). */
export function clampMediaSpeed(v: number): number {
  if (!Number.isFinite(v)) return 1;
  return Math.min(MEDIA_SPEED_MAX, Math.max(MEDIA_SPEED_MIN, v));
}

/** Запомненная скорость (media.speed); нет/битое значение — 1×. */
export function getMediaSpeed(): number {
  const raw = settingsStore.get(MEDIA_SPEED_KEY);
  if (raw === undefined || raw === '') return 1;
  return clampMediaSpeed(Number(raw));
}

/** Запомнить скорость НЕМЕДЛЕННО (кэш + отправка на сервер при связи). */
export function setMediaSpeed(v: number): void {
  settingsStore.set(MEDIA_SPEED_KEY, String(clampMediaSpeed(v)));
}

/** «Слушать подряд»: включено по умолчанию для подряд идущих голосовых/аудио. */
export function isAutoplayNextEnabled(): boolean {
  const raw = settingsStore.get(MEDIA_AUTOPLAY_NEXT_KEY);
  if (raw === undefined || raw === '') return true;
  return !(raw === 'false' || raw === '0');
}

export function setAutoplayNext(enabled: boolean): void {
  settingsStore.set(MEDIA_AUTOPLAY_NEXT_KEY, enabled ? 'true' : 'false');
}

/** Подписка на изменения настроек медиа (единый settingsStore). */
export function subscribeMediaSettings(fn: () => void): () => void {
  return settingsStore.subscribe(fn);
}

/** Хук: реактивные настройки медиа (после гидрации настроек — серверные). */
export function useMediaSettings(): { speed: number; autoplayNext: boolean } {
  const [state, setState] = useState(() => ({ speed: getMediaSpeed(), autoplayNext: isAutoplayNextEnabled() }));
  useEffect(() => subscribeMediaSettings(() => {
    setState((prev) => {
      const speed = getMediaSpeed();
      const autoplayNext = isAutoplayNextEnabled();
      if (prev.speed === speed && prev.autoplayNext === autoplayNext) return prev;
      return { speed, autoplayNext };
    });
  }), []);
  return state;
}
